import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Search, X, User, Shield, Building2, Calendar, Trophy, Loader2, CornerDownLeft, Clock, TrendingUp } from "lucide-react";

const GROUPS = [
  { key: "players", label: "Players", icon: User },
  { key: "teams", label: "Teams", icon: Shield },
  { key: "organizations", label: "Organizations", icon: Building2 },
  { key: "camps", label: "Camps", icon: Calendar },
  { key: "tournaments", label: "Tournaments", icon: Trophy },
];

const RECENT_KEY = "cbr_recent_searches";
const getRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; } };
const pushRecent = (t) => {
  if (!t || t.length < 2) return;
  const r = [t, ...getRecent().filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 5);
  localStorage.setItem(RECENT_KEY, JSON.stringify(r));
};
const clearRecent = () => localStorage.removeItem(RECENT_KEY);

const SUGGESTED = [
  { label: "Top Ranked Teams", url: "/live/team-rankings" },
  { label: "Recent Commitments", url: "/commitments" },
  { label: "Trending Players", url: "/movers" },
  { label: "Ontario Blue Jays", q: "Ontario Blue Jays" },
  { label: "CBR Nation", url: "/live" },
];
const TRENDING = [
  { label: "Top Ranked Teams", url: "/live/team-rankings" },
  { label: "Recent Commitments", url: "/commitments" },
  { label: "Movers & Risers", url: "/movers" },
  { label: "WWBA", q: "WWBA" },
  { label: "Ontario Blue Jays", q: "Ontario Blue Jays" },
  { label: "CBR Nation", url: "/live" },
];

const Chip = ({ children, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-100 hover:bg-[#E63946]/10 hover:text-[#E63946] text-slate-600 rounded-full transition-colors">
    {children}
  </button>
);

// Global command-palette search. Opens via the header button, Ctrl/Cmd+K, or "/".
export default function GlobalSearch({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState(getRecent());
  const inputRef = useRef();
  const navigate = useNavigate();

  // Flattened, ordered result list for keyboard navigation.
  const flat = [];
  if (res) GROUPS.forEach((g) => (res[g.key] || []).forEach((item) => flat.push({ ...item, group: g.key })));

  const close = useCallback(() => { setOpen(false); setQ(""); setRes(null); setActive(0); }, [setOpen]);

  const go = useCallback((item) => {
    if (item) { pushRecent(q.trim()); setRecent(getRecent()); navigate(item.url); close(); }
  }, [navigate, close, q]);

  const runSuggestion = (s) => {
    if (s.q) { setQ(s.q); inputRef.current?.focus(); }
    else if (s.url) { navigate(s.url); close(); }
  };

  // Keyboard shortcuts: Ctrl/Cmd+K or "/" to open; Esc to close.
  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable;
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || (e.key === "/" && !typing && !open)) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape" && open) {
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen, close]);

  useEffect(() => { if (open) { setRecent(getRecent()); setTimeout(() => inputRef.current?.focus(), 50); } }, [open]);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setRes(null); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q.trim())}`)
        .then(({ data }) => { setRes(data); setActive(0); })
        .catch(() => setRes(null))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(flat[active]); }
  };

  if (!open) return null;
  let idx = -1;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4" data-testid="global-search-modal">
      <div className="absolute inset-0 bg-[#0A192F]/60 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-xl bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center gap-3 px-4 border-b border-slate-100">
          <Search className="w-5 h-5 text-slate-400 shrink-0" />
          <input ref={inputRef} data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onInputKey}
            placeholder="Search players, teams, organizations, camps…"
            className="flex-1 py-4 text-[15px] outline-none placeholder-slate-400" />
          {loading && <Loader2 className="w-4 h-4 text-slate-300 animate-spin" />}
          <button onClick={close} data-testid="global-search-close" className="text-slate-400 hover:text-[#0A192F] shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {q.trim().length < 2 ? (
            <div className="px-4 py-5" data-testid="global-search-hint">
              {recent.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-overline text-slate-400 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Recent Searches</div>
                    <button data-testid="clear-recent-searches" onClick={() => { clearRecent(); setRecent([]); }} className="text-[11px] text-slate-400 hover:text-[#E63946]">Clear</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {recent.map((r) => <Chip key={r} testid="recent-search-chip" onClick={() => setQ(r)}>{r}</Chip>)}
                  </div>
                </div>
              )}
              <div>
                <div className="label-overline text-slate-400 flex items-center gap-1.5 mb-2"><TrendingUp className="w-3.5 h-3.5 text-[#E63946]" /> Trending</div>
                <div className="flex flex-wrap gap-2">
                  {TRENDING.map((s) => <Chip key={s.label} testid="trending-search-chip" onClick={() => runSuggestion(s)}>{s.label}</Chip>)}
                </div>
              </div>
              <div className="mt-5 text-[11px] text-slate-400 text-center">Tip: press <kbd className="px-1.5 py-0.5 bg-slate-100 rounded font-data">/</kbd> or <kbd className="px-1.5 py-0.5 bg-slate-100 rounded font-data">⌘K</kbd> anytime</div>
            </div>
          ) : res && res.total === 0 && !loading ? (
            <div className="px-4 py-6" data-testid="global-search-empty">
              <div className="text-sm font-semibold text-[#0A192F]">No results found for “{q}”.</div>
              <div className="text-xs text-slate-400 mt-1">Try searching for:</div>
              <div className="flex flex-wrap gap-3 mt-2">
                {GROUPS.map((g) => <span key={g.key} className="inline-flex items-center gap-1 text-xs text-slate-500"><g.icon className="w-3.5 h-3.5" />{g.label}</span>)}
              </div>
              <div className="label-overline text-slate-400 mt-5 mb-2">Suggested Searches</div>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED.map((s) => <Chip key={s.label} testid="suggested-search-chip" onClick={() => runSuggestion(s)}>{s.label}</Chip>)}
              </div>
            </div>
          ) : (
            GROUPS.map((g) => {
              const items = (res && res[g.key]) || [];
              if (!items.length) return null;
              return (
                <div key={g.key} data-testid={`global-search-group-${g.key}`}>
                  <div className="flex items-center gap-1.5 px-4 pt-3 pb-1 label-overline text-slate-400">
                    <g.icon className="w-3.5 h-3.5" /> {g.label}
                  </div>
                  {items.map((item) => {
                    idx += 1;
                    const isActive = idx === active;
                    return (
                      <button key={item.url + item.id} data-testid={`global-search-result-${g.key}`} onMouseEnter={() => setActive(idx)} onClick={() => go(item)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${isActive ? "bg-[#E63946]/5" : "hover:bg-slate-50"}`}>
                        <div className="w-9 h-9 rounded-sm overflow-hidden bg-slate-100 grid place-items-center shrink-0">
                          {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <g.icon className="w-4 h-4 text-slate-300" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-[#0A192F] truncate">{item.name}</div>
                          {item.descriptor && <div className="text-xs text-slate-400 truncate">{item.descriptor}</div>}
                        </div>
                        {isActive && <CornerDownLeft className="w-4 h-4 text-slate-300 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
